<template>
    <div class="org_settings">
        <nav id="org-setting-tab-nav" class="navbar navbar-expand primary-tab-bar">
            <div class="container-fluid">
                <div class="collapse navbar-collapse">
                    <ul class="navbar-nav mb-lg-0">
                        <li class="nav-item" v-for="tab_name in setting_tabs" :key="tab_name">
                            <a class="nav-link d-flex flex-nowrap primary-tab" 
                               :class="{ 'primary-tab-active': tab_name === setting_tab_name_active }"
                               href="#" 
                               role="button" 
                               aria-expanded="false" 
                               @click.prevent="setActiveTab(tab_name)">
                                <span>{{ tab_name }}</span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </nav>

        <div v-show="setting_tab_name_active === 'Connections'" v-if="canView('connections')">
            <connections-settings 
                :user="user" 
                :client="client" 
                :tab_name="tab_name" 
                :integrations="integrations" 
                :connections="connections" 
                :datablends="datablends" 
                :files="files" 
                :num_files="num_files">
            </connections-settings>
        </div>
        <div v-show="setting_tab_name_active === 'Audiences'" class="pathways-manage" v-if="canView('pathways') || canView('direct_mail')">
            <pathways-manage-segments 
                :key="'audiences-' + local_segments.length"
                :client="client" 
                :segments="local_segments"  
                :census_connections="census_connections" 
                :connection_datasets="connections" 
                :datasets="[]" 
                :datablends="datablends">
            </pathways-manage-segments>
        </div>
        <div v-show="setting_tab_name_active === 'Content'" v-if="canView('settings')">
            <content-settings 
                ref="contentSettings"
                :client="client" 
                :user="user">
            </content-settings>
        </div>
        <div v-show="setting_tab_name_active === 'Profile' || setting_tab_name_active === 'Overview'" v-if="canView('settings')">
            <profile-settings 
                :client="client" 
                :user="user">
            </profile-settings>
        </div>
        <div v-show="setting_tab_name_active === 'Team'" v-if="canView('settings')">
            <team-settings 
                :client="client" 
                :user="user">
            </team-settings>
        </div>
        <div v-show="setting_tab_name_active === 'Billing'" v-if="canView('billing')">
            <billing-settings 
                :entity="client" 
                :user="user">
            </billing-settings>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../mixins/permissions'
import ContentSettings from './organization_profile/ContentSettings.vue';

export default defineComponent({
    name: 'OrganizationSettings',

    mixins: [permissionsMixin],
    props: {
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        tab_name: {
            type: String,
            required: true
        },
        integrations: {
            type: Array,
            required: true
        },
        connections: {
            type: Array,
            required: true
        },
        datablends: {
            type: Array,
            required: true
        },
        files: {
            type: Array,
            required: true
        },
        num_files: {
            type: Number,
            required: true
        },
        census_connections: {
            type: Array,
            required: true
        },
        segments: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            setting_tabs: ['Overview', 'Team'],
            setting_tab_name_active: 'Overview',
            user_prioritization: [],
            local_segments: [],
        };
    },
    beforeMount() {
        if (this.client.plan_level === 'insights' || this.client.plan_level === 'trial') {
            this.setting_tabs = ['Overview', 'Content', 'Audiences','Connections', 'Team', 'Billing'];
        }

        // Filter tabs based on user permissions
        this.setting_tabs = this.setting_tabs.filter(tab => {
            if (tab === 'Connections') {
                return this.canView('connections');
            } else if (tab === 'Audiences') {
                return this.canView('pathways') || this.canView('direct_mail');
            } else if (tab === 'Content') {
                return this.canView('settings');
            } else if (tab === 'Profile' || tab === 'Overview') {
                return this.canView('settings');
            } else if (tab === 'Team') {
                return this.canView('settings');
            } else if (tab === 'Billing') {
                return this.canView('billing');
            }
            return true;
        });

        // get segment info to pass into Audiences tab
        this.loadSegmentsInfo();

        if ((this.tab_name === 'integrations' || this.tab_name === 'files' || this.tab_name === 'data_blends') && this.canView('connections')) {
            this.setting_tab_name_active = 'Connections';
        }
        //In case they click on the default landing page but don't have permissions to connections, show them profile
        else if ((this.tab_name === 'integrations' || this.tab_name === 'files' || this.tab_name === 'profile') && this.canView('settings')) {
            this.setting_tab_name_active = 'Overview';
        }
        else if (this.tab_name === 'content' && this.canView('settings')) {
            this.setting_tab_name_active = 'Content';
        }
        else if (this.tab_name === 'audiences' && (this.canView('pathways') || this.canView('direct_mail'))) {
            this.setting_tab_name_active = 'Audiences';
        }
        else if (this.tab_name === 'team' && this.canView('settings')) {
            this.setting_tab_name_active = 'Team';
        }
        else if (this.tab_name === 'billing' && this.canView('billing')) {
            this.setting_tab_name_active = 'Billing';
        }
    },
    watch: {
        tab_name: {
            handler(newTabName) {
                if ((newTabName === 'integrations' || newTabName === 'files' || newTabName === 'data_blends') && this.canView('connections')) {
                    this.setting_tab_name_active = 'Connections';
                } else if ((newTabName === 'integrations' || newTabName === 'files' || newTabName === 'profile') && this.canView('settings')) {
                    this.setting_tab_name_active = 'Overview';
                } else if (newTabName === 'content' && this.canView('settings')) {
                    this.setting_tab_name_active = 'Content';
                } else if (newTabName === 'audiences' && (this.canView('pathways') || this.canView('direct_mail'))) {
                    this.setting_tab_name_active = 'Audiences';
                } else if (newTabName === 'team' && this.canView('settings')) {
                    this.setting_tab_name_active = 'Team';
                } else if (newTabName === 'billing' && this.canView('billing')) {
                    this.setting_tab_name_active = 'Billing';
                }
            }
        },
        segments() {
            this.loadSegmentsInfo();
        },
        census_connections() {
            this.loadSegmentsInfo();
        },
        connections() {
            this.loadSegmentsInfo();
        },
    },
    methods: {
        setActiveTab(tab_name) {
            this.setting_tab_name_active = tab_name;
            let newURL = '/settings/' + this.client.url + '/';

            if (tab_name === 'Connections') {
                newURL += 'integrations';
            } else if (tab_name === 'Profile' || tab_name === 'Overview') {
                newURL += 'profile';
            } else if (tab_name === 'Content') {
                newURL += 'content';
            } else if (tab_name === 'Audiences') {
                newURL += 'audiences';
            } else if (tab_name === 'Team') {
                newURL += 'team';
            } else if (tab_name === 'Billing') {
                newURL += 'billing';
            }

            window.history.replaceState({}, {}, newURL);
            
            // Resize textareas when switching to Content tab
            if (tab_name === 'Content') {
                this.$nextTick(() => {
                    if (this.$refs.contentSettings) {
                        this.$refs.contentSettings.resizeAllTextareas();
                    }
                });
            }
        },
        loadSegmentsInfo() {
            if (!this.segments || !this.segments.length || !this.census_connections || !this.connections) {
                return;
            }
            const segments_temp = [];
            this.segments.forEach((segment) => {
                const pushed_pathways = [];
                this.census_connections.forEach((conn) => {
                    conn.syncs.forEach((conn_sync) => {
                        if (segment.id === conn_sync.segment_id) {
                            const connection = this.connections.find((c) => c.id === conn.connection_id);

                            if (connection && !pushed_pathways.find((pathway) => pathway.conn_id === connection.id)) { // remove duplicates
                                pushed_pathways.push({
                                    conn_id: connection.id,
                                    conn_name: connection.name,
                                    conn_icon: connection.integration.logo_url
                                });                                    
                            }
                        }
                    });
                });

                segment.pushed_pathways = pushed_pathways;
                segments_temp.push(segment);
            });
            this.local_segments = segments_temp;
        },
    }
});
</script>
